/**
 * Task Store
 * Zustand store for task management with CRUD operations.
 * Used by web admin to create, view, and manage volunteer tasks.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Task } from '@signalvote/shared';
import { TaskStatus } from '@signalvote/shared';

// ============================================================================
// Types
// ============================================================================

export interface TaskState {
  // State
  tasks: Task[];

  // CRUD Actions
  setTasks: (tasks: Task[]) => void;
  mergeApiTasks: (apiTasks: Task[]) => void;
  addTask: (task: Task) => void;
  updateTask: (id: string, updates: Partial<Task>) => void;
  updateTaskStatus: (taskId: string, status: TaskStatus) => void;
  deleteTask: (id: string) => void;

  // Getters
  getTaskById: (id: string) => Task | undefined;
  getTasksForDate: (date: string) => Task[];

  // Reset
  reset: () => void;
}

// ============================================================================
// Store
// ============================================================================

export const useTaskStore = create<TaskState>()(
  devtools(
    (set, get) => ({
      tasks: [],

      // Replace all tasks
      setTasks: (tasks) =>
        set({ tasks }, undefined, 'setTasks'),

      // Merge API tasks into the store (update existing by ID, add new ones)
      // Removes stale temp items that have been replaced by real server IDs.
      mergeApiTasks: (apiTasks) =>
        set(
          (state) => {
            const apiIdSet = new Set(apiTasks.map((t) => t.id));
            const existingIds = new Set(state.tasks.map((t) => t.id));
            const updatedTasks = state.tasks.map((t) => {
              const apiVersion = apiTasks.find((a) => a.id === t.id);
              return apiVersion ? { ...t, ...apiVersion } : t;
            });
            const newTasks = apiTasks.filter((a) => !existingIds.has(a.id));
            const merged = [...updatedTasks, ...newTasks];
            // Remove temp items no longer present in API response (replaced by real IDs)
            return { tasks: merged.filter((t) => !t.id.startsWith('temp-') || apiIdSet.has(t.id)) };
          },
          undefined,
          'mergeApiTasks'
        ),

      // Add a new task
      addTask: (task) =>
        set(
          (state) => ({ tasks: [...state.tasks, task] }),
          undefined,
          'addTask'
        ),

      // Update an existing task
      updateTask: (id, updates) =>
        set(
          (state) => ({
            tasks: state.tasks.map((t) =>
              t.id === id ? { ...t, ...updates } : t
            ),
          }),
          undefined,
          'updateTask'
        ),

      // Update a single task's status
      updateTaskStatus: (taskId, status) =>
        set(
          (state) => ({
            tasks: state.tasks.map((t) =>
              t.id === taskId ? { ...t, status } : t
            ),
          }),
          undefined,
          'updateTaskStatus'
        ),

      // Delete a task
      deleteTask: (id) =>
        set(
          (state) => ({ tasks: state.tasks.filter((t) => t.id !== id) }),
          undefined,
          'deleteTask'
        ),

      // Get a task by ID
      getTaskById: (id) => {
        return get().tasks.find((t) => t.id === id);
      },

      // Get all tasks for a specific date (YYYY-MM-DD)
      getTasksForDate: (date) => {
        return get().tasks.filter((t) => t.dueDate === date);
      },

      // Reset to empty
      reset: () =>
        set({ tasks: [] }, undefined, 'reset'),
    }),
    { name: 'task-store' }
  )
);
